import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import CalendarIntegration from './CalendarIntegration';
import SeguimientoAutoConfig from './SeguimientoAutoConfig';
import SeguimientoAutoResumen from './SeguimientoAutoResumen';
import FotosDeProductoConfig from './FotosDeProductoConfig';
import ModeloConversacionConfig from './ModeloConversacionConfig';
import { esEquipoPux } from '../../config/equipoPux';
import { SEGUIMIENTO_AUTO_HABILITADO } from '../../config/featureFlags';
import { permisosDelSeguimiento } from '../../utils/permisosDelSeguimiento';
import MailIntegration from './MailIntegration';
import EcommerceIntegration from './EcommerceIntegration';
import { getLimitsForPlan, normalizeAddons } from '../../utils/planHelpers';

export default function BotIntegrationsPanel({ user, botId, refreshTrigger = 0, context }) {
  const { t } = useTranslation('bots');
  const [config, setConfig] = useState(null);
  // Contador de recarga: lo incrementan los hijos que escriben por ENDPOINT en vez de
  // por cliente, para releer el documento sin pasar por `handleUpdate`.
  const [recarga, setRecarga] = useState(0);
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState(null);

  // Load plan data for integration slot checks
  useEffect(() => {
    if (!user) return;

    const loadPlanData = async () => {
      try {
        const ownerPath = context?.mode === 'organization' && context?.orgId
          ? `organizations/${context.orgId}`
          : `clientes/${user.uid}`;
        const ownerRef = doc(db, ownerPath);
        const ownerSnap = await getDoc(ownerRef);
        if (ownerSnap.exists()) {
          setPlanData(ownerSnap.data());
        }
      } catch (err) {
        console.warn('Error loading plan data for integration checks:', err);
      }
    };

    loadPlanData();
  }, [user, context]);

  /**
   * Check if a Starter plan user can enable an integration (calendar or ecommerce).
   * Starter gets 1 shared slot: calendar OR ecommerce (not both), unless they have the addon.
   * Returns { allowed: true } or { allowed: false, reason: string }
   */
  const checkIntegrationSlot = (feature) => {
    if (!planData) return { allowed: true }; // fail open if no data

    const planId = (planData.planId || planData.plan || 'starter').toString().toLowerCase();
    if (planId !== 'starter') return { allowed: true }; // Pro has unlimited slots

    const addons = normalizeAddons(planData.addonsActivos);
    const thisAddon = feature === 'calendar' ? 'addon_calendar' : 'addon_ecommerce';
    if ((addons[thisAddon] || 0) > 0) return { allowed: true }; // Addon purchased

    // Check if the OTHER feature is already active
    const otherFeature = feature === 'calendar' ? 'ecommerce' : 'calendar';
    const otherIsActive = config?.[otherFeature]?.provider && config[otherFeature].provider !== null;

    if (otherIsActive) {
      const otherName = otherFeature === 'calendar'
        ? t('integrations.calendarName')
        : t('integrations.ecommerceName');
      return {
        allowed: false,
        reason: t('integrations.starterExclusivity', { other: otherName })
      };
    }

    return { allowed: true };
  };

  // Load integrations config
  useEffect(() => {
    if (!user) return;

    const loadConfig = async () => {
      try {
        // Per-bot redesign (piece 2): calendar lives in the bot's own doc; mail/ecommerce
        // still in the org doc. Mirror getIntegrationsConfig's merge on the read side.
        const base = context?.mode === 'organization' && context?.orgId
          ? `organizations/${context.orgId}`
          : `clientes/${user.uid}`;

        const orgSnap = await getDoc(doc(db, `${base}/integrations/config`));
        const orgConfig = orgSnap.exists() ? orgSnap.data() : {};

        let botCalendar = { provider: null };
        let botMail = { provider: null };
        let botSeguimientoAuto = null;
        let botFotosDeProducto = null;
        let botModeloConversacion = null;
        if (botId) {
          const botSnap = await getDoc(doc(db, `${base}/bots/${botId}/integrations/config`));
          if (botSnap.exists()) {
            if (botSnap.data().calendar) botCalendar = botSnap.data().calendar;
            if (botSnap.data().mail) botMail = botSnap.data().mail;
            if (botSnap.data().seguimientoAuto) botSeguimientoAuto = botSnap.data().seguimientoAuto;
            if (botSnap.data().fotosDeProducto) botFotosDeProducto = botSnap.data().fotosDeProducto;
            if (botSnap.data().modeloConversacion) botModeloConversacion = botSnap.data().modeloConversacion;
          }
        }

        setConfig({
          ecommerce: { provider: null },
          ...orgConfig,
          calendar: botCalendar, // per-bot
          mail: botMail, // per-bot (Gmail migrated; SMTP/Outlook reconnect per-bot). ecommerce stays org.
          seguimientoAuto: botSeguimientoAuto, // per-bot (docs/SEGUIMIENTO.md §6.1); null = sin config, la UI pone los defaults
          fotosDeProducto: botFotosDeProducto, // per-bot; null = apagado (FotosDeProductoConfig)
          modeloConversacion: botModeloConversacion, // per-bot; null = OpenAI (piloto de modelos, sólo equipo PUX)
          organizationId: orgConfig.organizationId ?? null,
        });
      } catch (error) {
        console.error('Error loading integrations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [user, refreshTrigger, context, recarga]);

  /** Releer la configuración del bot. Para hijos que ya escribieron por su cuenta. */
  const recargarConfig = () => setRecarga((n) => n + 1);

  /**
   * Devuelve `true` si guardó y `false` si no; NO lanza (el aviso del error ya lo da aquí).
   * Un hijo que pinta el cambio antes de guardar —el interruptor de fotos— vuelve atrás con
   * `false`: antes no podía saberlo y se quedaba en la posición nueva (13-oct-2026).
   */
  const handleUpdate = async (updates) => {
    // GUARDA DE CONTRATO. Esta función ESCRIBE lo que le pasen; llamarla sin argumentos
    // —confundiéndola con «recargar»— reventaba en el destructuring de abajo y el usuario
    // recibía «Error al actualizar» por un fallo del front, después de una llamada al
    // backend que había ido bien. Ahora se distingue: sin cambios no hay nada que escribir.
    if (!updates || typeof updates !== 'object' || Array.isArray(updates)) {
      console.warn('[BotIntegrationsPanel] handleUpdate llamado sin cambios; ¿querías recargar?', updates);
      return false;
    }
    if (!user) return false;

    try {
      // Route writes by adapter — calendar + mail to the bot's own doc, ecommerce to the
      // org doc. Mirror of getIntegrationsConfig on the write side.
      const base = context?.mode === 'organization' && context?.orgId
        ? `organizations/${context.orgId}`
        : `clientes/${user.uid}`;

      // calendar + mail (incl. reminders, disconnect-optimistic) → per-bot doc
      const perBotUpdates = {};
      if ('calendar' in updates) perBotUpdates.calendar = updates.calendar;
      if ('mail' in updates) perBotUpdates.mail = updates.mail;
      if ('seguimientoAuto' in updates) perBotUpdates.seguimientoAuto = updates.seguimientoAuto;
      // Fotos de producto: por bot, como el seguimiento (lo lee `fotosActivadas` en functions).
      if ('fotosDeProducto' in updates) perBotUpdates.fotosDeProducto = updates.fotosDeProducto;
      // Modelo de la conversación: por bot (lo lee `modeloDeConversacion` en functions).
      if ('modeloConversacion' in updates) perBotUpdates.modeloConversacion = updates.modeloConversacion;
      if (Object.keys(perBotUpdates).length > 0 && botId) {
        await setDoc(
          doc(db, `${base}/bots/${botId}/integrations/config`),
          perBotUpdates,
          { merge: true }
        );
      }

      // everything else (ecommerce) → org doc
      const { calendar, mail, seguimientoAuto, fotosDeProducto, modeloConversacion, ...orgUpdates } = updates;
      if (Object.keys(orgUpdates).length > 0) {
        await setDoc(
          doc(db, `${base}/integrations/config`),
          { ...orgUpdates, organizationId: config?.organizationId ?? null },
          { merge: true }
        );
      }

      // Local state reflects the full merge regardless of destination doc
      setConfig({ ...config, ...updates, organizationId: config?.organizationId ?? null });
      return true;
    } catch (error) {
      console.error('Error updating integrations:', error);
      // EL MENSAJE REAL, no sólo el genérico. Un `TypeError` del propio front presentado
      // como «Error al actualizar. Intenta de nuevo.» es indepurable: el usuario reintenta,
      // vuelve a fallar igual, y no hay nada en pantalla que diga por dónde mirar.
      alert(`${t('integrations.updateError')}\n\n${error?.message || error}`);
      return false;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('integrations.loading')}</p>
        <style>{`
          .loading-container {
            text-align: center;
            padding: 3rem;
            color: #718096;
          }

          .spinner {
            width: 40px;
            height: 40px;
            margin: 0 auto 1rem;
            border: 4px solid #e2e8f0;
            border-top-color: #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="integrations-panel">
      <div className="panel-header">
        <h2>{t('integrations.title')}</h2>
        <p className="help-text">
          {t('integrations.subtitle')}
        </p>
      </div>

      <div className="integrations-list">
        <CalendarIntegration
          user={user}
          botId={botId}
          config={config}
          onUpdate={handleUpdate}
          onRecargar={recargarConfig}
          checkIntegrationSlot={checkIntegrationSlot}
        />

        <MailIntegration
          user={user}
          botId={botId}
          config={config}
          onUpdate={handleUpdate}
          organizationId={context?.mode === 'organization' ? context?.orgId : null}
        />

        <EcommerceIntegration
          user={user}
          config={config}
          onUpdate={handleUpdate}
          checkIntegrationSlot={checkIntegrationSlot}
          organizationId={context?.mode === 'organization' ? context?.orgId : null}
        />

        {botId && (
          <FotosDeProductoConfig
            config={config}
            onUpdate={handleUpdate}
          />
        )}

        {/* Piloto de modelos (13-oct-2026): sólo el equipo de PUX lo ve. */}
        {botId && esEquipoPux(user) && (
          <ModeloConversacionConfig
            config={config}
            onUpdate={handleUpdate}
          />
        )}

        {/* Seguimiento automático — oculto hasta el bloque 9 (docs/SEGUIMIENTO.md §7).
            Con el flag en false el JSX no se renderiza: no hay nada en el DOM. */}
        {/* La tarjeta sólo con canManageIntegrations (sin él no puede leer ni guardar su config);
            el resumen con canViewBots. Ver utils/permisosDelSeguimiento.js (20-oct-2026). */}
        {SEGUIMIENTO_AUTO_HABILITADO && permisosDelSeguimiento(context).tarjeta && (
          <SeguimientoAutoConfig
            config={config}
            onUpdate={handleUpdate}
            context={context}
          />
        )}
        {/* Resumen del mes por bot (19-oct-2026), detrás del MISMO flag. */}
        {SEGUIMIENTO_AUTO_HABILITADO && permisosDelSeguimiento(context).resumen && (
          <SeguimientoAutoResumen
            basePath={context?.mode === 'organization' && context?.orgId ? `organizations/${context.orgId}` : (user ? `clientes/${user.uid}` : null)}
            botId={botId}
            timeZone={config?.seguimientoAuto?.timeZone}
          />
        )}
        {SEGUIMIENTO_AUTO_HABILITADO && permisosDelSeguimiento(context).aviso && (
          <p className="seguimiento-gestionan-admins" data-testid="seguimiento-gestionan-admins">
            {t('integrations.seguimientoAuto.gestionanAdmins')}
          </p>
        )}
      </div>

      <div className="help-section">
        <h3>{t('integrations.howTitle')}</h3>
        <ul>
          <li>
            <strong>{t('integrations.calendarLabel')}</strong> {t('integrations.calendarDesc')}
          </li>
          <li>
            <strong>{t('integrations.emailLabel')}</strong> {t('integrations.emailDesc')}
          </li>
          <li>
            <strong>{t('integrations.ecommerceLabel')}</strong> {t('integrations.ecommerceDesc')}
          </li>
        </ul>

        <div className="security-note">
          <strong>{t('integrations.securityTitle')}</strong> {t('integrations.securityText')}
        </div>
      </div>

      <style>{`
        .integrations-panel {
          max-width: 900px;
        }

        .panel-header {
          margin-bottom: 2rem;
        }

        .panel-header h2 {
          margin: 0 0 0.5rem 0;
          color: #2d3748;
        }

        .help-text {
          color: #718096;
          font-size: 0.95rem;
          margin: 0;
        }

        .integrations-list {
          margin-bottom: 2rem;
        }

        .integration-card.coming-soon {
          opacity: 0.6;
          pointer-events: none;
          background: #f7fafc;
        }

        .badge-soon {
          display: inline-block;
          margin-left: 0.5rem;
          padding: 0.25rem 0.75rem;
          background: #feebc8;
          color: #7c2d12;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .help-section {
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin-top: 2rem;
        }

        .help-section h3 {
          margin: 0 0 1rem 0;
          color: #2d3748;
          font-size: 1.1rem;
        }

        .help-section ul {
          list-style: none;
          padding: 0;
          margin: 0 0 1rem 0;
        }

        .help-section li {
          padding: 0.5rem 0;
          color: #4a5568;
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .help-section code {
          background: #edf2f7;
          padding: 0.125rem 0.375rem;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.85em;
          color: #2d3748;
        }

        .security-note {
          background: #ebf8ff;
          border: 1px solid #bee3f8;
          border-radius: 6px;
          padding: 1rem;
          color: #2c5282;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
}
